import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, fmtNum, monthStart, today } from "@/lib/api";
import { Card } from "@/components/kit";
import { Clock, Truck, Package, Wallet, TrendingUp } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "amber", testid }) => {
  const tones = {
    amber: "text-amber-400 bg-amber-500/10",
    sky: "text-sky-400 bg-sky-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    violet: "text-violet-400 bg-violet-500/10",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon size={20} />
      </div>
      <div className="font-display text-2xl font-bold text-white">{value}</div>
      <div className="mt-0.5 text-xs uppercase tracking-wider text-slate-500">{label}</div>
    </Card>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [range] = useState({ from: monthStart(), to: today() });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/entries", { params: { date_from: range.from, date_to: range.to } })
      .then((r) => setEntries(r.data)).catch(() => {});
  }, [range]);

  const totals = entries.reduce(
    (a, e) => {
      a.hours += e.work_hours || 0;
      a.machineHours += (e.machines || []).reduce((s, m) => s + (m.hours || 0), 0);
      a.machine += e.cost?.machine || 0;
      a.material += e.cost?.material || 0;
      a.total += e.cost?.total || 0;
      return a;
    },
    { hours: 0, machineHours: 0, machine: 0, material: 0, total: 0 }
  );
  const isEmployee = user?.role === "employee";

  return (
    <div className="rise">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-500">Översikt · denna månad</p>
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Hej {user?.name?.split(" ")[0]}</h1>
        <p className="mt-1 text-sm text-slate-400">
          {range.from} – {range.to} · {entries.length} {isEmployee ? "egna rapporter" : "rapporter"}{!isEmployee && ` · ${projects.length} projekt`}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-hours" icon={Clock} label="Arbetstimmar" value={fmtNum(totals.hours) + " h"} tone="amber" />
        <Stat testid="stat-machine-hours" icon={Truck} label="Maskintimmar" value={fmtNum(totals.machineHours) + " h"} tone="sky" />
        {user?.role === "owner" ? (
          <>
            <Stat testid="stat-material" icon={Package} label="Materialkostnad" value={fmt(totals.material)} tone="violet" />
            <Stat testid="stat-total" icon={Wallet} label="Summa (exkl. moms)" value={fmt(totals.total)} tone="emerald" />
          </>
        ) : (
          <>
            <Stat testid="stat-reports" icon={Package} label="Rapporter" value={entries.length} tone="violet" />
            <Stat testid="stat-projects" icon={Wallet} label="Projekt" value={projects.length} tone="emerald" />
          </>
        )}
      </div>

      <Card className="mt-6 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-4">
          <TrendingUp size={16} className="text-amber-400" />
          <h2 className="font-display text-base font-bold text-white">{isEmployee ? "Mina senaste rapporter" : "Senaste rapporter"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="px-5 py-3">Datum</th>
                {!isEmployee && <th className="px-5 py-3">Anställd</th>}
                <th className="px-5 py-3">Projekt</th>
                <th className="px-5 py-3 text-right">Timmar</th>
                {user?.role === "owner" && <th className="px-5 py-3 text-right">Belopp</th>}
              </tr>
            </thead>
            <tbody data-testid="dashboard-entries">
              {entries.slice(0, 8).map((e) => {
                const p = projects.find((x) => x.id === e.project_id);
                return (
                  <tr key={e.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="px-5 py-3 font-mono text-slate-300">{e.date}</td>
                    {!isEmployee && <td className="px-5 py-3 text-slate-200">{e.employee_name}</td>}
                    <td className="px-5 py-3 text-slate-400">{p?.name || "—"}</td>
                    <td className="px-5 py-3 text-right text-slate-300">{fmtNum(e.work_hours)}</td>
                    {user?.role === "owner" && <td className="px-5 py-3 text-right font-semibold text-white">{fmt(e.cost?.total)}</td>}
                  </tr>
                );
              })}
              {entries.length === 0 && (
                <tr><td colSpan={isEmployee ? 3 : user?.role === "owner" ? 5 : 4} className="px-5 py-10 text-center text-slate-500">Inga rapporter denna månad.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
